import React, { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, Clock, Scissors, Sparkles, Star, ScanFace, Droplet } from 'lucide-react';

const iconMap = {
  scissors: Scissors,
  sparkles: Sparkles,
  scan: ScanFace,
  star: Star,
  droplet: Droplet
};

const CLOSE_ANIMATION_MS = 1100;

const ServiceModal = ({ service, serviceKey, isOpen, onClose, t }) => {
  const [isClosing, setIsClosing] = useState(false);

  const requestClose = useCallback(() => {
    if (isClosing) return;
    setIsClosing(true);
    window.setTimeout(() => {
      onClose();
    }, CLOSE_ANIMATION_MS);
  }, [isClosing, onClose]);

  const handleEsc = useCallback((e) => {
    if (e.key === 'Escape') requestClose();
  }, [requestClose]);

  // Reset closing flag whenever modal becomes freshly open with a new service
  useEffect(() => {
    if (isOpen) setIsClosing(false);
  }, [isOpen, service]);

  useEffect(() => {
    if (!isOpen) return;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen, handleEsc]);

  if (!isOpen || !service) return null;

  const IconComponent = iconMap[service.icon];

  const backdropClass = isClosing ? 'service-modal-backdrop-out' : 'service-modal-backdrop';
  const cardClass = isClosing ? 'service-modal-card-out' : 'service-modal-card';

  return createPortal(
    <div
      data-testid="service-modal-backdrop"
      onClick={requestClose}
      className={`fixed inset-0 z-[100] flex items-center justify-center px-4 py-8 ${backdropClass}`}
      style={{ background: 'rgba(0,0,0,0.88)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}
    >
      <div
        data-testid="service-modal-card"
        onClick={(e) => e.stopPropagation()}
        className={`${cardClass} relative w-full max-w-md sm:max-w-lg max-h-[92vh] overflow-y-auto bg-gradient-to-b from-neutral-900 via-neutral-950 to-black border border-neutral-700 rounded-lg shadow-2xl`}
        style={{
          boxShadow: '0 25px 80px -10px rgba(255,255,255,0.08), 0 0 0 1px rgba(255,255,255,0.05)'
        }}
      >
        {/* Premium subtle gradient ring (Pokemon-card holo effect) */}
        <div className="pointer-events-none absolute inset-0 rounded-lg opacity-40"
          style={{
            background: 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, transparent 30%, transparent 70%, rgba(255,255,255,0.06) 100%)'
          }}
        />

        {/* Close button */}
        <button
          data-testid="service-modal-close-btn"
          onClick={requestClose}
          aria-label="Close"
          className="absolute top-3 right-3 z-10 w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center rounded-full bg-black/70 border border-neutral-700 text-white hover:bg-white hover:text-black hover:border-white transition-all duration-200 backdrop-blur-sm"
        >
          <X className="w-4 h-4 sm:w-5 sm:h-5" strokeWidth={2} />
        </button>

        {/* Hero image */}
        <div className="relative w-full aspect-[4/3] sm:aspect-[16/10] overflow-hidden rounded-t-lg">
          <img
            src={service.image}
            alt={t(`servicesData.${serviceKey}.title`)}
            decoding="async"
            fetchpriority="high"
            className="w-full h-full object-cover"
          />
          {/* Image overlay gradient */}
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
          {/* Icon badge */}
          <div className="absolute top-4 left-4 p-2.5 rounded-sm bg-black/70 border border-neutral-700 backdrop-blur-sm">
            {IconComponent && <IconComponent className="w-5 h-5 text-white" strokeWidth={1.5} />}
          </div>
        </div>

        {/* Content */}
        <div className="relative px-6 sm:px-8 py-6 sm:py-8">
          <h3
            data-testid="service-modal-title"
            className="text-3xl sm:text-4xl font-oswald font-bold text-white uppercase tracking-tight mb-4 leading-tight"
          >
            {t(`servicesData.${serviceKey}.title`)}
          </h3>

          <p
            data-testid="service-modal-description"
            className="text-gray-400 text-sm sm:text-base leading-relaxed font-montserrat font-light mb-8"
          >
            {t(`servicesData.${serviceKey}.description`)}
          </p>

          {/* Price + Duration row */}
          <div className="flex justify-between items-end pt-6 border-t border-neutral-800 mb-6">
            <div>
              <p className="text-gray-500 text-[10px] uppercase tracking-[0.25em] font-montserrat mb-1">
                {t('servicesModal.priceLabel')}
              </p>
              <span
                data-testid="service-modal-price"
                className="text-3xl sm:text-4xl font-oswald font-bold text-white"
              >
                {service.price}
              </span>
            </div>
            <div className="text-right">
              <p className="text-gray-500 text-[10px] uppercase tracking-[0.25em] font-montserrat mb-1">
                {t('servicesModal.durationLabel')}
              </p>
              <span
                data-testid="service-modal-duration"
                className="inline-flex items-center gap-2 text-base sm:text-lg text-gray-200 font-montserrat font-medium"
              >
                <Clock className="w-4 h-4" strokeWidth={1.5} />
                {service.duration}
              </span>
            </div>
          </div>

          {/* Booking CTA */}
          <a
            data-testid="service-modal-book-btn"
            href="https://n608855.alteg.io/"
            target="_blank"
            rel="noopener noreferrer"
            className="block w-full text-center bg-white text-black hover:bg-neutral-200 transition-all duration-300 py-4 rounded-sm font-oswald uppercase tracking-wider text-sm sm:text-base font-bold"
          >
            {t('servicesModal.bookNow')}
          </a>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ServiceModal;
